'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Minus, Plus, ShoppingCart, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Separator } from '@kit/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@kit/ui/sheet';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { useCartContext } from '../hooks/cart-context';
import { useCheckout } from '../hooks/use-checkout';
import { formatArticlePrice } from '../lib/format-price';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  shopId: string;
  currency: string;
  customerEmail?: string;
}

export function CartDrawer({
  isOpen,
  onClose,
  shopId,
  currency,
  customerEmail,
}: CartDrawerProps) {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const {
    items,
    removeItem,
    updateQuantity,
    clearCart,
    totalItems,
    totalPrice,
  } = useCartContext();

  const { isPending, error, startCheckout, clearError } = useCheckout({
    shopId,
    currency,
    customerEmail,
  });

  // Show toast on checkout error (once per error), then clear it so it does
  // not re-fire on subsequent renders. `error` is an i18n key from useCheckout.
  useEffect(() => {
    if (error) {
      toast.error(t(error));
      clearError();
    }
  }, [error, t, clearError]);

  async function handleCheckout() {
    if (items.length === 0) return;

    const checkoutItems = items.map((item) => ({
      article_id: item.article_id,
      quantity: item.quantity,
    }));

    const result = await startCheckout(checkoutItems);

    if (result.checkout_url) {
      clearCart();
      router.push(result.checkout_url);
    } else if (result.order_id) {
      clearCart();
      router.push(`/home/orders/${result.order_id}`);
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            <Trans i18nKey="shop:cart" defaults="Cart" />
            {totalItems > 0 && (
              <span className="bg-primary text-primary-foreground rounded-md px-2 py-0.5 text-xs font-medium">
                {totalItems}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
            <ShoppingCart className="text-muted-foreground h-12 w-12" />
            <p className="text-muted-foreground text-sm">
              <Trans i18nKey="shop:cartEmpty" defaults="Your cart is empty" />
            </p>
          </div>
        ) : (
          <>
            {/* Items list */}
            <div className="flex-1 overflow-y-auto py-4">
              <ul className="space-y-4">
                {items.map((item) => (
                  <li key={item.article_id} className="flex items-start gap-3">
                    <div className="flex-1 space-y-1">
                      <p className="line-clamp-2 text-sm leading-snug font-medium">
                        {item.title}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {formatArticlePrice(
                          item.price,
                          currency,
                          i18n.language,
                        )}
                      </p>
                    </div>

                    {/* Quantity controls */}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                          updateQuantity(item.article_id, item.quantity - 1)
                        }
                        aria-label={t('shop:decreaseQuantity')}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-6 text-center text-sm tabular-nums">
                        {item.quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                          updateQuantity(item.article_id, item.quantity + 1)
                        }
                        aria-label={t('shop:increaseQuantity')}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Remove button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive h-7 w-7 shrink-0"
                      onClick={() => removeItem(item.article_id)}
                      aria-label={t('shop:removeItem')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4 border-t pt-4">
              {/* Subtotal */}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm">
                  <Trans i18nKey="shop:subtotal" defaults="Subtotal" />
                </span>
                <span className="font-semibold">
                  {formatArticlePrice(totalPrice, currency, i18n.language)}
                </span>
              </div>

              <Separator />

              {/* Checkout button */}
              <Button
                className="w-full"
                disabled={isPending}
                onClick={() => void handleCheckout()}
              >
                {isPending ? (
                  <Trans i18nKey="shop:processing" defaults="Processing..." />
                ) : (
                  <Trans i18nKey="shop:checkout" defaults="Checkout" />
                )}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
